<template>
  <div class="loading-splash" :class="{ 'fade-out': fadeOut }">
    <div class="loading-content">
      <div class="logo-container">
        <LogoAnimated 
          :width="300" 
          :height="171" 
          :repeat="1"
          @animation-complete="handleAnimationComplete"
        />
      </div>
      
   
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, watch } from 'vue'
import { useAppStateStore } from '../stores/appState'
import LogoAnimated from './LogoAnimated.vue'

const fadeOut = ref(false)

const isLoadingData = inject('isLoadingData', ref(false))
const dataLoadingCompleted = inject('dataLoadingCompleted', ref(false))
const animationCompleted = inject('animationCompleted', ref(false))

const appStateStore = useAppStateStore()

const emit = defineEmits(['animation-complete', 'fade-complete'])

const dataLoaded = computed(() => dataLoadingCompleted.value)

// Track when animation has completed and we can start checking for data readiness
const canCheckForDataReadiness = ref(false)

const handleAnimationComplete = () => {
  emit('animation-complete')
  
  // Preserve the 300ms delay for UX timing, then enable data checking
  setTimeout(() => {
    canCheckForDataReadiness.value = true
  }, 300)
}

const checkReadinessAndHide = () => {
  // Data is loaded, check if we need to coordinate with panel entrance
  if (appStateStore.pendingPanelEntrance) {
    // There's a pending panel entrance, coordinate timing for smooth transition
    handlePanelEntranceCoordination()
  } else {
    // Normal page, hide loading immediately
    startFadeOut()
  }
}

const handlePanelEntranceCoordination = () => {
  if (!appStateStore.shouldLoadingWaitForPanel()) {
    // Panel data is ready, hide loading and let panel entrance begin
    startFadeOut()
  }
  // If panel data not ready, the watcher below will handle it reactively
}

// ✅ REACTIVE: Replace polling with watchers for immediate response
// Watch for data loading completion after animation completes
watch([canCheckForDataReadiness, dataLoaded], ([canCheck, isDataLoaded]) => {
  if (canCheck && isDataLoaded) {
    checkReadinessAndHide()
  }
})

// Watch for panel data readiness (replaces polling in handlePanelEntranceCoordination)
watch(() => appStateStore.shouldLoadingWaitForPanel(), (shouldWait) => {
  // Only proceed if we're in panel coordination mode and data becomes ready
  if (!shouldWait && appStateStore.pendingPanelEntrance && canCheckForDataReadiness.value) {
    startFadeOut()
  }
})

const startFadeOut = () => {
  fadeOut.value = true
  
  // Emit fade-complete after the 500ms transition completes
  setTimeout(() => {
    emit('fade-complete')
  }, 500)
}
</script>

<style lang="scss" scoped>
@use '../assets/scss/variables' as *;
@use '../assets/scss/typography' as *;
@use '../assets/scss/mixins' as m;

.loading-splash {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: $xw-teal-light;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  opacity: 1;
  transition: opacity 0.5s ease-out;
  
  &.fade-out {
    opacity: 0;
  }
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.logo-container {
  margin-bottom: $spacing-xl;
  filter: drop-shadow(0 0 20px rgba(255, 255, 255, 0.3));
  transform: scale(1.6);

  @include m.below-tablet-landscape {
     transform: scale(1.3);
  }
}

@media (max-width: $breakpoint-sm) {
  .logo-container {
    margin-bottom: $spacing-lg;
  }
}
</style> 